import { Fragment, useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import Label from '../../components/Label'
import DraggableList from '../../components/DraggableList'
import Prompt from '../../components/Prompt'
import Page from '../../components/Page'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import useContentful from '../../contexts/useContentful'
import useQualtricsSurvey from '../../hooks/useQualtricsSurvey'
import { DragDropContext } from 'react-beautiful-dnd';
// import useRecaptcha from "../../hooks/useRecaptcha";

const RESPONSE_ORDER_QUALTRICS_IDS = [
  'QID182',
  'QID184',
  'QID185',
  'QID186',
  'QID187',
  'QID188',
  'QID189',
  'QID190',
  'QID191',
  'QID192',
]

const RESPONSE_ORDER_CHOICE_IDS = [
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
]

const TOGGLE_IDS = [
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
]

const QUESTION_ONE_IDS = [
  '4',
  '5',
  '6',
]

const RuralPage = () => {
  const navigate = useNavigate()
  const { surveyID } = useParams()
  const {
    questionOne,
    questionOneOptions,
    questionTwo,
    questionTwoOptions,
    questionThreeLabel,
    questionThree,
    questionThreeRankLabelMost,
    questionThreeRankLabelLeast,
    questionThreeOptions,
    questionFour,
    buttonText,
    questionLabel,
    textFieldPlaceholder,
    errorText,
  } = useContentful()
  const qualtricsQuestions = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.qualtricsQuestions)
  const responses = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.responses)
  const { 
    submitMultipleChoiceAnswer, 
    submitBulkMultipleChoiceAnswers, 
    submitSingleAnswer,
    submitMultiSelectAnswer,
    advanceSurvey,
    qualtricsLoading,
  } = useQualtricsSurvey()
  const [value, setValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [tryAgainError, setTryAgainError] = useState(false)
  // const { isVerified, error, executeRecaptcha } = useRecaptcha();

const advanceSurveyPromise = (params, questionId, answer) => {
  return new Promise((resolve, reject) => {
    advanceSurvey(
      {
        ...params,
        cb: (error) => {
          if (error) {
            reject(error)
          } else {
            resolve()
          }
        }
      },
      questionId,
      answer
    )
  })
}

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      // const response = await executeRecaptcha("rural");
      // await advanceSurveyPromise({ 
      //   // score: response?.score
      // }, 'QID183', value);
      // await advanceSurveyPromise({ 
      //   // score: response?.score
      // });
      // await advanceSurveyPromise({ 
      //   // score: response?.score
      // });
      await advanceSurveyPromise({ 
        nextIndex: 6,
        // score: response?.score
      });

      setTryAgainError(false);
      navigate(`/imagine/${surveyID}/1`);
    } catch (error) {
      setTryAgainError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const items = useMemo(() => {
    return questionThreeOptions?.map((o, idx) => ({ 
      text: o, 
      choiceId: RESPONSE_ORDER_CHOICE_IDS[idx],
      dragId: `draggable${idx}`
    })) || []
  }, [questionThreeOptions, qualtricsQuestions])

  const [itemOrder, setItemOrder] = useState(items)

  useEffect(() => {
    setItemOrder(items);
  }, [items]);

  const submitNewRanking = result => {
    if(!result.destination) return

    const newItems = [...itemOrder]
    const [reorderedItem] = newItems.splice(result.source.index, 1)
    newItems.splice(result.destination.index, 0, reorderedItem)
    setItemOrder(newItems)

    submitBulkMultipleChoiceAnswers(RESPONSE_ORDER_QUALTRICS_IDS, newItems.map(o => o.choiceId))
  }

  return (
    <Page>
      <Stack
        py={4}
      >
        <Container>
          <Stack
            gap={4}
          >
            <Typography
              variant="h4Bold"
              color="text.neutralDark"
              maxWidth="785px"
            >
              {questionLabel} 1
            </Typography>
            <Typography
              variant="h4Bold"
              color="text.neutralDark"
              maxWidth="785px"
            >
              {questionOne}
            </Typography>
            <Stack
              gap={2}
            >
              {questionOneOptions?.map((o, idx) => (
                <Stack
                  key={`solution${idx}`}
                  display="grid"
                  gridTemplateColumns="auto 1fr"
                  gap={1.5}
                  alignItems="center"
                  alignContent="center"
                >
                  <Radio
                    size="medium"
                    checked={QUESTION_ONE_IDS[idx] === responses.QID180}
                    onChange={() => submitMultipleChoiceAnswer('QID180', QUESTION_ONE_IDS[idx])}
                  />
                  <Typography
                    variant="body1"
                    color="text.neutralDark"
                  >
                    {o}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Divider />
            <Stack
              gap={4}
            >
              <Typography
                variant="h4Bold"
                color="text.neutralDark"
                maxWidth="785px"
              >
                {questionLabel} 2
              </Typography>
              <Typography
                variant="h4Bold"
                color="text.neutralDark"
                maxWidth="785px"
              >
                {questionTwo}
              </Typography>
              <Stack
                gap={1.5}
              >
                <Divider
                  sx={{
                    borderColor: 'neutral.light',
                  }}
                />
                {questionTwoOptions?.map((o, idx) => (
                  <Fragment
                    key={`questionTwo${o.fields?.optionText}`}
                  >
                    <Stack
                      display="grid"
                      gap={2}
                      gridTemplateColumns="auto 1fr"
                      alignItems="center"
                      alignContent="center"
                    >
                      <Switch
                        checked={!!responses[`QID181_${TOGGLE_IDS[idx]}`]}
                        onChange={() => submitMultiSelectAnswer('QID181', TOGGLE_IDS[idx], !!!responses?.[`QID181_${TOGGLE_IDS[idx]}`], responses)}
                      />
                      <Stack>
                        <Typography
                          variant="body1Medium"
                        >
                          {o.fields?.optionText}
                        </Typography>
                        {!!o.fields?.optionDescription?.length && (
                          <Typography
                            variant="body1"
                            color="text.neutralLight"
                            sx={{
                              fontStyle: 'italic',
                            }}
                          >
                            {o.fields?.optionDescription}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                    <Divider
                      sx={theme => ({
                        borderColor: theme.palette.neutral.light,
                      })}
                    />
                  </Fragment>
                ))}
              </Stack>
            </Stack>
            <Stack
              gap={4}
            >
              <Typography
                variant="h4"
              >
                {questionThreeLabel}
              </Typography>
              <Typography
                variant="h4Bold"
                color="text.neutralDark"
                maxWidth="785px"
              >
                {questionThree}
              </Typography>
              <DragDropContext onDragEnd={submitNewRanking}>
                <Stack
                  display="grid"
                  sx={(theme)=>({
                    justifyContent: 'start',
                    '@media (min-width: 768px)': {
                      justifyContent: 'center',
                    },
                  })}

                >
                  <DraggableList
                    items={itemOrder}
                    mostLabel={questionThreeRankLabelMost}
                    leastLabel={questionThreeRankLabelLeast}
                  />
                </Stack>
              </DragDropContext>
            </Stack>
            <Divider />
            <Stack
              gap={1}
            >
              <Label>
                {questionFour}
              </Label>
              <TextField
                multiline
                rows={3}
                placeholder={textFieldPlaceholder}
                value={value}
                onChange={e => {
                  setValue(e.target.value)
                  // debouncedSubmitTextAnswer('QID183', e.target.value)
                }}
              />
            </Stack>
            <Stack
              display="grid"
              justifyContent="center"
            >
              <Button
                sx={{
                  position: 'relative',
                }}
                disabled={(isLoading || qualtricsLoading)}
                onClick={onSubmit}
              >
                <span
                  style={{
                    opacity: (isLoading || qualtricsLoading) ? 0 : 1,
                  }}
                >
                  {buttonText}
                </span>
                {(isLoading || qualtricsLoading) && (
                  <CircularProgress 
                    size={25}
                    sx={{
                      position: 'absolute',
                    }}
                  />
                )}
              </Button>
              {tryAgainError && (
                <Typography
                  variant="body1"
                  color="error"
                  mt={2}
                  textAlign="center"
                >
                  {errorText}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Container>
      </Stack>
      <Prompt />
    </Page>
  )
}

export default RuralPage
